/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * UIButtonLabel: Renders a Material UI Button with label, optional icons, and navigation/click logic.
 */

import { Button } from '@mui/material'
import type { TUIButtonLabel } from './types'
import { useClickHandler, renderIcon } from './utils'
import { DEFAULT_TYPE } from './constants'

/**
 * Label button component that displays text with optional start/end icons.
 * Supports onClick callbacks and external links (href).
 */
export default function UIButtonLabel({
  loading,
  variant,
  fullWidth,
  disabled,
  startIcon,
  endIcon,
  color,
  size,
  type = DEFAULT_TYPE,
  label,
  testId,
  active,
  tabRole = true,
  onClick,
  href,
}: TUIButtonLabel) {
  const isDisabled = !!disabled || !!loading
  const handleClick = useClickHandler({ onClick, href }, isDisabled)

  const isTab = variant === 'tab'
  const isTabRole = isTab && tabRole

  return (
    <Button
      data-testid={testId}
      loading={!!loading}
      variant={isTab ? 'text' : variant}
      fullWidth={!!fullWidth}
      disabled={isDisabled}
      startIcon={renderIcon(startIcon)}
      endIcon={renderIcon(endIcon)}
      color={color}
      size={size}
      type={type}
      disableRipple
      className={
        isTab
          ? `UIButton-tab${active ? ' UIButton-tab-active' : ''}`
          : undefined
      }
      // `variant="tab"` is `UITabs`' own idiom (a `role="tablist"` `Stack` of
      // these) — without `role="tab"`/`aria-selected`, that tablist's plain
      // `<button>` children fail the `aria-required-children` rule
      // (jest-axe, plan task 5.3 part 1). `tabRole={false}` opts a
      // tab-styled button with no `tablist` ancestor (an overflow trigger)
      // out, since `role="tab"` there would fail `aria-required-parent`
      // instead.
      role={isTabRole ? 'tab' : undefined}
      aria-selected={isTabRole ? !!active : undefined}
      onClick={handleClick}
    >
      {label}
    </Button>
  )
}
